// filepath: apps/web/src/features/admin/fieldVisibility/RoleFieldVisibilityPage.tsx
//
// FR-UI-ADM-4 — top-level page composition for the admin viewer.
//
// Composes:
//   - useRoleFieldVisibility (the TanStack Query hook)
//   - RoleFieldVisibilityTable (the pure-render table)
//   - 5 FR-UI-CORE-10 fetch states + success state.
//
// Read-only: there is no save / submit / mutation path. The viewer
// surfaces a classification snapshot for the (roleName,
// entityType) pair; FR-UI-ADM-2 will edit the underlying
// permissionset via a separate surface.
//
// INV-9: no dangerouslySetInnerHTML anywhere. Server strings
// render as text nodes via RoleFieldVisibilityTable.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import {
  classifyRoleFieldVisibilityError,
  useRoleFieldVisibility,
} from "./useRoleFieldVisibility";
import { RoleFieldVisibilityTable } from "./RoleFieldVisibilityTable";
import type { RoleFieldVisibilityError } from "./types";
import type { Locale } from "@/i18n/useTranslation";

interface RoleFieldVisibilityPageProps {
  /** The target role name (URL path param). */
  roleName: string;
  /** The target entity type (URL query param). */
  entityType: string;
  /** Active locale — forwarded to the table for i18n-aware labels. */
  locale: Locale;
}

/**
 * RoleFieldVisibilityPage — admin viewer page. Composes the
 * hook + table + 5 fetch states. No save / mutation path —
 * the viewer is read-only by contract.
 */
export function RoleFieldVisibilityPage({
  roleName,
  entityType,
  locale,
}: RoleFieldVisibilityPageProps): React.ReactElement {
  const { t } = useTranslation();
  const query = useRoleFieldVisibility(roleName, entityType);

  // ── loading ──────────────────────────────────────────────────────────
  if (query.isLoading) {
    return (
      <div
        className="space-y-4 animate-pulse p-6"
        data-testid="loading-skeleton"
        aria-busy="true"
        aria-live="polite"
      >
        <div className="h-12 bg-muted rounded" />
        <div className="h-32 bg-muted rounded" />
      </div>
    );
  }

  // ── error states ─────────────────────────────────────────────────────
  if (query.error) {
    const err = classifyRoleFieldVisibilityError(query.error);
    return <ErrorState error={err} onRetry={() => query.refetch()} />;
  }

  // ── success ──────────────────────────────────────────────────────────
  if (query.data === undefined) {
    return (
      <p className="text-muted-foreground text-sm p-6" data-testid="no-data">
        {t("admin.field_visibility.empty", "No data.")}
      </p>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="role-field-visibility-page">
      <header>
        <h1 className="text-lg font-semibold" data-testid="page-title">
          {t("admin.field_visibility.title", "Field visibility", {
            roleName,
            entityType,
          })}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t(
            "admin.field_visibility.subtitle",
            "Which fields does this role see?",
            {
              roleName,
              entityType,
            },
          )}
        </p>
      </header>
      <RoleFieldVisibilityTable data={query.data} locale={locale} />
    </div>
  );
}

// --- ErrorState component ------------------------------------------------

interface ErrorStateProps {
  error: RoleFieldVisibilityError;
  onRetry: () => void;
}

function ErrorState({ error, onRetry }: ErrorStateProps): React.ReactElement {
  const { t } = useTranslation();

  const title = ((): string => {
    switch (error.kind) {
      case "permission_denied":
        return t("admin.errors.permission_denied");
      case "rate_limit":
        return t("admin.errors.rate_limit");
      case "stale_version":
        return t("admin.errors.stale_version");
      case "network":
        return t("admin.errors.network");
      case "fetch_failure":
      default:
        return t("admin.errors.fetch_failure");
    }
  })();

  return (
    <div
      className="space-y-4 p-6"
      data-testid="error-state"
      data-error-kind={error.kind}
    >
      <p className="text-sm text-destructive">{title}</p>
      <p className="text-xs text-muted-foreground">{error.message}</p>
      {error.retriable && (
        <button
          type="button"
          className="rounded bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
          data-testid="retry-button"
          onClick={onRetry}
        >
          {t("common.retry", "Retry")}
        </button>
      )}
    </div>
  );
}
